import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import axios from 'axios';
import AbbrTip from '@/components/AbbrTip';
import { CoverageTrendBadge, ThinCoverageBadge, coverageTrend, isStaleCoverage } from '@/components/AnalystCoverageBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ANALYST_GUIDE_PATH = '/learn/analyst-coverage-who-why';

// Round 94a (Feb 2026) — Stacked bar chart for Finnhub recommendation
// trends. Hover any segment to see the exact analyst count for that
// month and category. Modeled visually after Finnhub's own widget
// but rendered with our SVG + Tailwind so it matches the rest of the
// Stock Modal (no embed iframe, no theme mismatch).

const CATEGORIES = [
  { key: 'strong_buy',  label: 'Strong Buy',  color: '#16a34a' }, // green-600
  { key: 'buy',         label: 'Buy',         color: '#86efac' }, // green-300
  { key: 'hold',        label: 'Hold',        color: '#eab308' }, // yellow-500
  { key: 'sell',        label: 'Sell',        color: '#fb923c' }, // orange-400
  { key: 'strong_sell', label: 'Strong Sell', color: '#dc2626' }, // red-600
];

function formatPeriod(period) {
  if (!period) return '';
  // Finnhub returns YYYY-MM-DD (first day of month)
  try {
    const d = new Date(period + (period.length === 10 ? 'T00:00:00' : ''));
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return period; }
}

function RecBadge({ rec }) {
  if (!rec || rec === 'none') return <span className="text-xs font-mono text-muted-foreground">No coverage</span>;
  const colors = {
    strong_buy: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    buy: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    hold: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    sell: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    strong_sell: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  };
  const label = rec.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span
      data-testid="analyst-rec-badge"
      className={`inline-flex px-2 py-0.5 rounded-full text-[12px] font-bold ${colors[rec] || 'bg-muted text-muted-foreground'}`}
    >
      {label}
    </span>
  );
}

function StackedBar({ trends }) {
  const containerRef = useRef(null);
  const [tip, setTip] = useState(null); // { period, category, value, x, y }

  const chart = useMemo(() => {
    if (!trends || trends.length === 0) return null;
    // Render most-recent on the RIGHT (Finnhub returns most-recent first;
    // reverse for left→right reading like a normal timeline).
    const rows = [...trends].slice(0, 6).reverse();
    const totals = rows.map((r) => CATEGORIES.reduce((s, c) => s + (r[c.key] || 0), 0));
    const maxTotal = Math.max(1, ...totals);
    const width = 360;
    const height = 180;
    const padTop = 12;
    const padBottom = 28;
    const padLeft = 24;
    const padRight = 8;
    const chartW = width - padLeft - padRight;
    const chartH = height - padTop - padBottom;
    const groupW = chartW / rows.length;
    const barW = Math.min(38, groupW * 0.62);
    const barGap = (groupW - barW) / 2;
    const yScale = (v) => (v / maxTotal) * chartH;

    const segments = rows.map((r, i) => {
      const x = padLeft + i * groupW + barGap;
      let stackTop = padTop + chartH;
      const segs = CATEGORIES.map((cat) => {
        const v = Number(r[cat.key] || 0);
        if (v <= 0) return null;
        const h = yScale(v);
        const y = stackTop - h;
        stackTop = y;
        return { x, y, w: barW, h, value: v, category: cat, period: r.period };
      }).filter(Boolean);
      return { x, barW, period: r.period, total: totals[i], segs };
    });

    // 3 y-axis ticks for orientation
    const ticks = [0, Math.ceil(maxTotal / 2), maxTotal].map((v) => ({
      v, y: padTop + chartH - yScale(v),
    }));

    return { width, height, padTop, padBottom, padLeft, segments, ticks, chartH };
  }, [trends]);

  if (!chart) return null;

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      onMouseLeave={() => setTip(null)}
      data-testid="analyst-stacked-bar"
    >
      <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        {chart.ticks.map((t, i) => (
          <g key={`tick-${i}`}>
            <line
              x1={chart.padLeft}
              x2={chart.width - 8}
              y1={t.y}
              y2={t.y}
              className="stroke-border/40"
              strokeDasharray="2,3"
            />
            <text x={chart.padLeft - 4} y={t.y + 3} textAnchor="end" fontSize="9" fontFamily="IBM Plex Mono, monospace" className="fill-muted-foreground">
              {t.v}
            </text>
          </g>
        ))}
        {chart.segments.map((grp, gi) => (
          <g key={`grp-${gi}`}>
            {grp.segs.map((seg, si) => (
              <rect
                key={`seg-${gi}-${si}`}
                x={seg.x}
                y={seg.y}
                width={seg.w}
                height={Math.max(1, seg.h)}
                fill={seg.category.color}
                data-testid={`analyst-bar-seg-${grp.period}-${seg.category.key}`}
                onMouseEnter={(e) => {
                  const rect = containerRef.current.getBoundingClientRect();
                  const svgX = ((e.clientX - rect.left) / rect.width) * chart.width;
                  const svgY = ((e.clientY - rect.top) / rect.height) * chart.height;
                  setTip({
                    period: grp.period,
                    category: seg.category,
                    value: seg.value,
                    total: grp.total,
                    x: svgX,
                    y: svgY,
                  });
                }}
                onMouseMove={(e) => {
                  const rect = containerRef.current.getBoundingClientRect();
                  const svgX = ((e.clientX - rect.left) / rect.width) * chart.width;
                  const svgY = ((e.clientY - rect.top) / rect.height) * chart.height;
                  setTip((prev) => (prev ? { ...prev, x: svgX, y: svgY } : prev));
                }}
                style={{ cursor: 'pointer' }}
              />
            ))}
            {/* Total count label above each bar */}
            <text
              x={grp.x + grp.barW / 2}
              y={(grp.segs[grp.segs.length - 1]?.y ?? (chart.padTop + chart.chartH)) - 4}
              textAnchor="middle"
              fontSize="9"
              fontFamily="IBM Plex Mono, monospace"
              className="fill-foreground font-bold"
            >
              {grp.total}
            </text>
            {/* X-axis period label */}
            <text
              x={grp.x + grp.barW / 2}
              y={chart.height - 8}
              textAnchor="middle"
              fontSize="10"
              fontWeight="700"
              fontFamily="IBM Plex Mono, monospace"
              className="fill-muted-foreground"
            >
              {formatPeriod(grp.period).slice(0, 3)} {formatPeriod(grp.period).slice(-2)}
            </text>
          </g>
        ))}
      </svg>

      {tip && (
        <div
          className="absolute z-10 pointer-events-none bg-popover border rounded-md shadow-lg px-2.5 py-1.5 text-[12px]"
          style={{
            left: `${Math.min((tip.x / chart.width) * 100, 80)}%`,
            top: `${Math.max((tip.y / chart.height) * 100 - 18, 0)}%`,
          }}
          data-testid="analyst-bar-tooltip"
        >
          <p className="font-bold text-foreground leading-tight">{formatPeriod(tip.period)}</p>
          <p className="flex items-center gap-1.5 mt-0.5">
            <span
              className="inline-block w-2 h-2 rounded-sm"
              style={{ backgroundColor: tip.category.color }}
            />
            <span className="text-muted-foreground font-bold">{tip.category.label}:</span>
            <span className="font-mono font-bold text-foreground">{tip.value}</span>
            <span className="text-muted-foreground font-bold text-[11px]">/ {tip.total}</span>
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-between mt-2 px-1 gap-1" data-testid="analyst-bar-legend">
        {CATEGORIES.map((cat) => (
          <div key={cat.key} className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: cat.color }} />
            <span className="text-[11px] text-muted-foreground font-bold whitespace-nowrap">{cat.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatActionDate(ds) {
  if (!ds) return '';
  try {
    const d = new Date(ds);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, '${String(d.getFullYear()).slice(2)}`;
  } catch { return ds; }
}

function actionColor(action) {
  const a = (action || '').toLowerCase();
  if (a.includes('up')) return 'text-green-600 dark:text-green-400';
  if (a.includes('down')) return 'text-red-600 dark:text-red-400';
  return 'text-muted-foreground';
}

export default function AnalystCoverageCard({ ticker, onNavigateWithOrigin }) {
  const navigate = useNavigate();
  const openGuide = () => {
    if (onNavigateWithOrigin) onNavigateWithOrigin(ANALYST_GUIDE_PATH);
    else navigate(ANALYST_GUIDE_PATH);
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/stocks/${ticker}/analyst`, { withCredentials: true, timeout: 15000 });
        if (!cancelled) setData(r.data || {});
      } catch {
        if (!cancelled) setData({ source: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [ticker]);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card px-4 py-3 break-inside-avoid" data-testid="analyst-coverage-loading">
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400 mt-0 mb-1.5">Analyst Coverage</h4>
        <p className="text-xs font-bold text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!data || data.source === 'none' || data.source === 'error' || !data.recommendation) {
    return null; // Don't render an empty card — Massive-sourced sections still show.
  }
  // Sep 2026 — latest monthly bar older than a year = no current coverage; hide silently.
  if (isStaleCoverage(data.latest_period)) return null;

  const trend = coverageTrend(data.trends, data.latest_period);
  const hasTrends = Array.isArray(data.trends) && data.trends.length > 0;
  const hasTargets = data.target_high != null || data.target_mean != null || data.target_low != null;
  const hasActions = Array.isArray(data.recent_actions) && data.recent_actions.length > 0;
  const sourceLabel = null; // Round 95d v2 — source tag removed per user request

  return (
    <div className="rounded-lg border bg-card px-4 py-3 break-inside-avoid" data-testid="analyst-coverage-card">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">
          <AbbrTip term="RECOMMENDATION">Analyst Coverage</AbbrTip>
        </h4>
      </div>

      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <RecBadge rec={data.recommendation} />
          {data.num_analysts != null && data.num_analysts > 0 && (
            <span className="text-xs font-mono font-semibold text-foreground" data-testid="analyst-count">
              {data.num_analysts} <span className="font-normal text-muted-foreground">analysts</span>
            </span>
          )}
          <CoverageTrendBadge trend={trend} />
          <ThinCoverageBadge count={data.num_analysts} />
        </div>
        {data.latest_period && (
          <span className="text-[11px] text-muted-foreground font-mono font-bold">{formatPeriod(data.latest_period)}</span>
        )}
      </div>

      {hasTrends && <StackedBar trends={data.trends} />}

      {hasTargets && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="analyst-targets">
          {data.target_low != null && (
            <div className="rounded-md border px-2 py-1.5 text-center" data-testid="analyst-target-low">
              <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Low</p>
              <p className="text-sm font-bold font-mono">${Number(data.target_low).toFixed(2)}</p>
            </div>
          )}
          {data.target_mean != null && (
            <div className="rounded-md border border-fuchsia-500/40 bg-fuchsia-500/5 px-2 py-1.5 text-center" data-testid="analyst-target-mean">
              <p className="text-[9px] font-bold text-fuchsia-500 dark:text-fuchsia-400 uppercase tracking-wider">Mean</p>
              <p className="text-sm font-bold font-mono">${Number(data.target_mean).toFixed(2)}</p>
            </div>
          )}
          {data.target_high != null && (
            <div className="rounded-md border px-2 py-1.5 text-center" data-testid="analyst-target-high">
              <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">High</p>
              <p className="text-sm font-bold font-mono">${Number(data.target_high).toFixed(2)}</p>
            </div>
          )}
        </div>
      )}

      <div className="mt-2.5 flex justify-center">
        <button
          type="button"
          onClick={openGuide}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline cursor-pointer"
          data-testid="analyst-coverage-guide-link"
          title="Open the Learn guide on analyst coverage. Back button returns here with this stock modal reopened."
        >
          <BookOpen className="w-3 h-3" />
          Analyst Coverage - who does What and Why
        </button>
      </div>

      {hasActions && (
        <div className="mt-3" data-testid="analyst-recent-actions">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Recent Actions</p>
          <div className="space-y-0.5">
            {data.recent_actions.slice(0, 3).map((a, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-2 text-[11px] py-0.5 border-b border-border/20 last:border-0"
                data-testid={`analyst-action-${i}`}
              >
                <span className="font-medium text-foreground truncate flex-1" title={a.company}>
                  {a.company || '—'}
                </span>
                <span className={`font-mono font-bold ${actionColor(a.action)}`}>
                  {a.action || '—'}
                </span>
                <span className="text-muted-foreground font-mono text-[10px]">{formatActionDate(a.date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <FmpExtras fmp={data.fmp} mean={data.target_mean} />
    </div>
  );
}

// Round 94b (Feb 2026) — FMP-sourced extras: price-target evolution
// across 4 timeframes + actual analyst grade actions (firm name +
// "upgrade/downgrade/maintain/initiate"). Only renders when the FMP
// budget gate let the call through and the payload is non-empty.
function FmpExtras({ fmp, mean }) {
  if (!fmp || (fmp.fmp_budget_exhausted && !fmp.target_all_time_avg)) return null;

  const targetRows = [
    { label: '1M',  avg: fmp.target_last_month_avg,   count: fmp.target_last_month_count },
    { label: '1Q',  avg: fmp.target_last_quarter_avg, count: fmp.target_last_quarter_count },
    { label: '1Y',  avg: fmp.target_last_year_avg,    count: fmp.target_last_year_count },
    { label: 'ALL', avg: fmp.target_all_time_avg,     count: fmp.target_all_time_count },
  ].filter((r) => r.avg != null && Number(r.avg) > 0);

  const hasGrades = Array.isArray(fmp.grades) && fmp.grades.length > 0;
  const hasPubs = Array.isArray(fmp.publishers) && fmp.publishers.length > 0;

  if (targetRows.length === 0 && !hasGrades) return null;

  return (
    <div className="mt-3 pt-3 border-t border-border/40" data-testid="analyst-fmp-extras">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Price Target Evolution
        </p>
        <span className="text-[9px] text-muted-foreground font-mono font-bold" data-testid="fmp-source-label">
          via FMP{fmp.fmp_served_stale ? ' (cached)' : ''}
        </span>
      </div>

      {targetRows.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5" data-testid="fmp-target-grid">
          {targetRows.map((r) => {
            // Color the avg vs current mean — bullish (above mean) =
            // green, bearish (below) = red, neutral within 2% = muted.
            let color = 'text-foreground';
            if (mean && Number(mean) > 0) {
              const delta = (Number(r.avg) - Number(mean)) / Number(mean);
              if (delta > 0.02) color = 'text-green-600 dark:text-green-400';
              else if (delta < -0.02) color = 'text-red-600 dark:text-red-400';
            }
            return (
              <div
                key={r.label}
                className="rounded-md border px-1.5 py-1 text-center"
                data-testid={`fmp-target-${r.label.toLowerCase()}`}
              >
                <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">{r.label}</p>
                <p className={`text-[12px] font-bold font-mono ${color}`}>${Number(r.avg).toFixed(0)}</p>
                <p className="text-[9px] font-bold text-muted-foreground font-mono">{r.count || 0}</p>
              </div>
            );
          })}
        </div>
      )}

      {hasGrades && (
        <div className="mt-3" data-testid="fmp-grades">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
            Recent Grades
          </p>
          <div className="space-y-0.5">
            {fmp.grades.slice(0, 4).map((g, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-2 text-[11px] py-0.5 border-b border-border/20 last:border-0"
                data-testid={`fmp-grade-${i}`}
              >
                <span className="font-bold text-foreground truncate flex-1" title={g.firm}>
                  {g.firm || '—'}
                </span>
                <span className="text-muted-foreground font-mono font-bold text-[10px] truncate max-w-[120px]" title={`${g.prev_grade || ''} → ${g.new_grade || ''}`}>
                  {g.new_grade || '—'}
                </span>
                <span className={`font-mono font-bold ${actionColor(g.action)}`}>
                  {g.action || '—'}
                </span>
                <span className="text-muted-foreground font-mono font-bold text-[10px]">{formatActionDate(g.date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasPubs && (
        <p className="mt-2 text-[9px] text-muted-foreground font-bold leading-tight" data-testid="fmp-publishers">
          <span className="font-bold">Sources:</span> {fmp.publishers.slice(0, 8).join(' · ')}
        </p>
      )}
    </div>
  );
}
